import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, MapPin } from 'lucide-react';
import { LocationMap } from '../../components/LocationMap';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

export const LocationFormDialog = ({
  open, onClose, editItem, form, setForm,
  onSave, saving, managerEmployees,
}) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle className="font-heading">{editItem ? 'Standort bearbeiten' : 'Neuer Standort'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 pt-4">
        <div className="space-y-2">
          <Label>Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Hauptgebäude" data-testid="location-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Adresse *</Label>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
            placeholder="Musterstraße 1" data-testid="location-address-input" />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>PLZ *</Label>
            <Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
              data-testid="location-postal-input" />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Stadt *</Label>
            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="location-city-input" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Telefon</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>E-Mail</Label>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Standortleiter/in</Label>
          <Select value={form.manager_id || "none"} onValueChange={(v) => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Kein Leiter</SelectItem>
              {managerEmployees.length > 0 ? (
                (() => {
                  const dk = getDuplicateNameKeys(managerEmployees);
                  return managerEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{formatLastFirst(emp, dk)}</SelectItem>
                  ));
                })()
              ) : (
                <div className="px-2 py-1.5 text-sm text-muted-foreground">Keine aktiven Mitarbeiter vorhanden</div>
              )}
            </SelectContent>
          </Select>
          {managerEmployees.length === 0 && (
            <p className="text-xs text-muted-foreground">Erstellen Sie zuerst einen Mitarbeiter.</p>
          )}
        </div>

        {/* GPS Validation */}
        <div className="border-t pt-4 mt-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="font-medium flex items-center gap-2">
                <MapPin className="h-4 w-4" />GPS-Zeiterfassung
              </h4>
              <p className="text-sm text-muted-foreground">Mitarbeiter müssen vor Ort sein, um sich einzustempeln</p>
            </div>
            <Switch checked={form.gps_validation_enabled} onCheckedChange={(v) => setForm({ ...form, gps_validation_enabled: v })} />
          </div>
          {form.gps_validation_enabled && (
            <div className="space-y-3">
              <LocationMap
                latitude={form.latitude} longitude={form.longitude}
                radius={form.gps_radius_meters || 100}
                onLocationSelect={(lat, lng) => setForm({ ...form, latitude: lat.toFixed(6), longitude: lng.toFixed(6) })}
                editable={true}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Breitengrad (Latitude)</Label>
                  <Input type="number" step="0.000001" placeholder="z.B. 52.520008"
                    value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Längengrad (Longitude)</Label>
                  <Input type="number" step="0.000001" placeholder="z.B. 13.404954"
                    value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Radius (Meter)</Label>
                <Input type="number" min="10" max="1000" placeholder="100"
                  value={form.gps_radius_meters}
                  onChange={(e) => setForm({ ...form, gps_radius_meters: parseInt(e.target.value) || 100 })} />
                <p className="text-xs text-muted-foreground mt-1">Erlaubte Entfernung vom Standort (10-1000m)</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
          <Label>Aktiv</Label>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full">Abbrechen</Button>
          <Button onClick={onSave} disabled={saving} className="rounded-full" data-testid="save-location-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editItem ? 'Aktualisieren' : 'Erstellen')}
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);
